import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import { useMemo } from 'react'
import { VAT_CODE_OPTIONS, buildCreateProductSchema } from '../types'
import type { CreateProductFormData } from '../types'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { productsApi } from '@/api/endpoints/products'
import { getErrorMessage } from '@/shared/utils/error-codes'
import { ApiError } from '@/api/types'

interface ProductFormProps {
  mode: 'create'
  onCancel: () => void
}

export function ProductForm({ onCancel }: ProductFormProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')

  const createProductSchema = useMemo(
    () => buildCreateProductSchema(tProducts),
    [tProducts],
  )

  const form = useForm<CreateProductFormData>({
    resolver: zodResolver(createProductSchema),
    defaultValues: {
      name: '',
      publicName: '',
      vatCode: 'AT20',
      active: true,
      temperatureChoice: true,
      barcode: '',
    },
  })

  const createMutation = useMutation({
    mutationFn: productsApi.create,
    onSuccess: (data) => {
      toast.success(tProducts('toast.createSuccess'))
      queryClient.invalidateQueries({ queryKey: ['products'] })
      const sku = data.sku
      if (sku) {
        navigate({ to: `/products/${sku}` })
      } else {
        navigate({ to: '/products' })
      }
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error(tProducts('toast.createFailed'))
      }
    },
  })

  const onSubmit = (data: CreateProductFormData) => {
    const payload = { ...data }
    if (!payload.publicName || !payload.publicName.trim())
      delete payload.publicName
    if (!payload.barcode || !payload.barcode.trim()) delete payload.barcode
    createMutation.mutate(payload)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormItem>
          <FormLabel>{tProducts('fields.sku')}</FormLabel>
          <FormControl>
            <Input value={tProducts('form.skuValue')} disabled />
          </FormControl>
          <FormDescription>{tProducts('form.skuDescription')}</FormDescription>
        </FormItem>

        <FormField
          control={form.control}
          name="barcode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tProducts('fields.barcodeOptional')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={tProducts('form.barcodePlaceholder')}
                  {...field}
                  disabled={createMutation.isPending}
                />
              </FormControl>
              <FormDescription>
                {tProducts('form.barcodeDescription')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="publicName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tProducts('fields.menuName')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={tProducts('form.menuNamePlaceholder')}
                  {...field}
                  disabled={createMutation.isPending}
                />
              </FormControl>
              <FormDescription>
                {tProducts('form.menuNameDescription')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tProducts('fields.productName')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={tProducts('form.productNamePlaceholder')}
                  {...field}
                  disabled={createMutation.isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="vatCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tProducts('fields.vatCode')}</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={createMutation.isPending}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={tProducts('form.vatCodePlaceholder')}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {VAT_CODE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                {tProducts('form.vatCodeDescription')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={createMutation.isPending}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>{tCommon('active')}</FormLabel>
                <FormDescription>
                  {tProducts('form.activeDescription')}
                </FormDescription>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="temperatureChoice"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={createMutation.isPending}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>{tProducts('fields.temperatureChoice')}</FormLabel>
                <FormDescription>
                  {tProducts('form.temperatureChoiceDescription')}
                </FormDescription>
              </div>
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={createMutation.isPending}
          >
            {tCommon('cancel')}
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending
              ? tCommon('creating')
              : tProducts('form.createAction')}
          </Button>
        </div>
      </form>
    </Form>
  )
}
